import React, { FC, Fragment, useEffect, useState, useMemo, useCallback } from 'react';
import TableTanStack from './TableTanStack';
import { OrderLog, OrderLogDisplay } from 'ts/shared/interfaces/MOResponse';
import { dateFormat } from 'utils/shared/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faMinus, faCopy } from '@fortawesome/free-solid-svg-icons';
import { replaceHtmlToStr, showSuccess } from 'utils/utils';
import Button from 'components/ui/Button/Button';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import PrettyCode from './PrettyCode';
import { tryFormatJson } from 'utils/shared/my-order';
import { moLogTable } from 'constants/mo';

interface MoLogTableProps {
  data: OrderLog[];
  onRowDoubleClick?: (index: number) => void;
  onRowClick?: (index: number) => void;
}

const MoLogTable: FC<MoLogTableProps> = ({ data }) => {
  const [displayedData, setDisplayedData] = useState<OrderLogDisplay[]>();
  const [selectedLog, setSelectedLog] = useState<OrderLogDisplay>();
  const [preview, setPreview] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [highlightLine, setHighlightLine] = useState<number>(-1);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  useEffect(() => {
    const displayedData = data.map((log, index) => ({
      ...log,
      index: index + 1,
      dateC: dateFormat(log.dateC, 'dd/MM/yyyy HH:mm:ss'),
      directionCodeJsx: <FontAwesomeIcon icon={faMinus} size="lg" />,
      actionCodeJsx: <FontAwesomeIcon icon={faDownload} size="sm" />,
    }));
    setDisplayedData(displayedData);
    if (displayedData.length) {
      setSelectedLog(displayedData[0]);
      const logBody = displayedData[0]?.logBody;
      const logBodyCleaned = replaceHtmlToStr(logBody || '')
        .replace(/\\r\\n/g, '\n')
        .replace(/\\n/g, '\n');
      const formatted = tryFormatJson(logBodyCleaned);

      setPreview(formatted);
      setSearchTerm('');
      setHighlightLine(-1);
    }
  }, [data]);

  const handleShowPreview = (logId: number) => {
    const newSelectedLog = displayedData?.find((log) => log.orderLogId === logId);

    if (newSelectedLog) {
      setSelectedLog(newSelectedLog);
      const logBody = newSelectedLog?.logBody;
      const logBodyCleaned = replaceHtmlToStr(logBody || '')
        .replace(/\\r\\n/g, '\n')
        .replace(/\\n/g, '\n');
      const formatted = tryFormatJson(logBodyCleaned);

      setPreview(formatted);
      setSearchTerm('');
      setHighlightLine(-1);
    }
  };

  const handleRowDoubleClick = (logId: number) => {
    const newSelectedLog = displayedData?.find((log) => log.orderLogId === logId);

    if (newSelectedLog) {
      const logBody = newSelectedLog?.logBody;

      if (navigator.clipboard) {
        navigator.clipboard.writeText(
          typeof logBody === 'string' ? logBody : JSON.stringify(logBody)
        );
        showSuccess('Copied to clipboard!');
      }
    }
  };

  const lines = useMemo(() => {
    return preview ? preview.split('\n') : [];
  }, [preview]);

  const findLineWithSearchTerm = useCallback(
    (searchTerm: string): number => {
      if (!searchTerm || lines.length === 0) return -1;

      const searchLower = searchTerm.toLowerCase();
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].toLowerCase().includes(searchLower)) {
          return i + 1;
        }
      }
      return -1;
    },
    [lines]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearchTerm && preview && debouncedSearchTerm.length > 0) {
      const lineNumber = findLineWithSearchTerm(debouncedSearchTerm);
      setTimeout(() => {
        setHighlightLine(lineNumber);
      }, 300);
    } else {
      setTimeout(() => {
        setHighlightLine(-1);
      }, 300);
    }
  }, [debouncedSearchTerm]); // eslint-disable-line

  return (
    <Fragment>
      <div className="mo-table-wr mo-main-table-wr">
        <TableTanStack
          data={displayedData || []}
          columns={moLogTable}
          onDoubleClick={handleRowDoubleClick}
          onClick={handleShowPreview}
          globalFilter={''}
          hideSort={false}
          tableId="mo-log"
          activeId={selectedLog?.orderLogId}
        />
      </div>

      {preview && selectedLog !== undefined && (
        <div>
          <div
            className="flex-row mb-5 mt-5 flex-row-end w100p mo-log-search"
            style={{ background: 'var(--color-bg-lightest)' }}
          >
            <FormGroup
              label="Search log..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e);
              }}
              movablePlaceholderSm
              movablePlaceholder="Search log..."
            />
            <Button
              icon={faDownload}
              title="Save Xml"
              className="btn btn-default btn-ssm ml-5"
              onClick={() => {
                const blob = new Blob([selectedLog?.logBody || ''], {
                  type: 'application/xml',
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'log.xml';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
            />
            <Button
              icon={faCopy}
              title="Copy"
              className="btn btn-default btn-ssm ml-5"
              onClick={() => {
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(selectedLog?.logBody || '');
                  showSuccess('Copied to clipboard!');
                }
              }}
            />
          </div>
          <div className="mo-log">
            <PrettyCode code={preview} language="json" highlightLine={highlightLine} />
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default MoLogTable;
